/**
 * <s-welcome-offer> — First-visit email signup popup
 * Opens its <dialog> once per browser for new visitors, reopens after the
 * signup form posts (to show the code or errors), and copies the code.
 *
 * Attributes:
 *   data-code      — discount code revealed after signup (from theme settings)
 *   data-preview   — always open (theme editor homepage preview)
 *   data-copy-label / data-copy-success / data-copy-error — copy button labels
 *
 * Commands (dispatched on this element via commandfor):
 *   --copy-code — copy data-code to the clipboard
 */
const SEEN_KEY = 'welcome-offer-seen-v3';

class SWelcomeOffer extends HTMLElement {
  #ac;
  #dialog;
  #openTimer;
  #resetTimer;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (e) => {
      if (e.command === '--copy-code' && e.source instanceof HTMLElement) this.#copy(e.source);
    }, opts);

    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => this.#remember(), opts);

    this.#bindCard(opts);

    if (this.querySelector('[data-welcome-offer-result]')) {
      // Phones: a successful signup reveals the code on the homepage newsletter card instead of the popup.
      const success = this.querySelector('[data-welcome-offer-success]');
      if (success && this.dataset.code && window.matchMedia('(max-width: 767.98px)').matches) {
        this.#remember();
        if (!document.querySelector('s-newsletter-flip [data-newsletter-back]')) {
          const root = window.Shopify?.routes?.root || '/';
          window.location.assign(`${root}?customer_posted=true`);
        }
        return;
      }
      this.#open();
      if (this.dataset.code && this.querySelector('[data-welcome-offer-code]')) {
        this.#openTimer = setTimeout(() => this.#flip(), 700);
      }
      return;
    }

    const preview = this.hasAttribute('data-preview');
    if (!preview && this.#seen()) return;
    this.#openTimer = setTimeout(() => this.#open(), preview ? 300 : 1200);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#resetTimer);
  }

  #open() {
    if (this.#dialog.open || document.querySelector('dialog:modal')) return;
    this.#dialog.showModal();
  }

  /** Flips the gift card to reveal the discount code on its back after a successful signup. */
  #flip() {
    const card = this.querySelector('[data-welcome-card]');
    const back = this.querySelector('[data-welcome-card-back]');
    if (!card || !back) return;
    card.removeAttribute('data-active');
    card.style.setProperty('--rx', '0deg');
    card.style.setProperty('--ry', '0deg');
    card.setAttribute('data-flipped', '');
    back.setAttribute('aria-hidden', 'false');
    card.querySelector('.welcome-card__inner')?.setAttribute('aria-hidden', 'true');
  }

  /** Tilts the gift card toward the pointer and moves its glare; clicking it focuses the email field. */
  #bindCard(opts) {
    const card = this.querySelector('[data-welcome-card]');
    if (!card) return;

    card.addEventListener('click', () => {
      // Theme editor only: clicking the card flips it so the back can be previewed without signing up.
      if (this.hasAttribute('data-preview') && this.querySelector('[data-welcome-card-back]')) {
        if (card.hasAttribute('data-flipped')) {
          card.removeAttribute('data-flipped');
          this.querySelector('[data-welcome-card-back]').setAttribute('aria-hidden', 'true');
          card.querySelector('.welcome-card__inner')?.removeAttribute('aria-hidden');
        } else {
          this.#flip();
        }
        return;
      }
      const email = this.querySelector('input[type="email"]');
      if (email) email.focus();
      else this.querySelector('[data-welcome-offer-code]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, opts);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const reset = () => {
      card.removeAttribute('data-active');
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
      card.style.setProperty('--gx', '50%');
      card.style.setProperty('--gy', '30%');
    };

    card.addEventListener('pointermove', (e) => {
      if (card.hasAttribute('data-flipped')) return;
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      card.setAttribute('data-active', '');
      card.style.setProperty('--ry', `${((x - 0.5) * 18).toFixed(2)}deg`);
      card.style.setProperty('--rx', `${((0.5 - y) * 14).toFixed(2)}deg`);
      card.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`);
      card.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`);
    }, opts);

    card.addEventListener('pointerleave', reset, opts);
    card.addEventListener('blur', reset, opts);
  }

  #seen() {
    try {
      return localStorage.getItem(SEEN_KEY) === '1';
    } catch {
      return false;
    }
  }

  #remember() {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* storage unavailable: the popup may show again next visit */
    }
  }

  async #copy(button) {
    const code = this.dataset.code;
    if (!code) return;
    let copied = false;
    try {
      if (typeof navigator.clipboard?.writeText === 'function') {
        await navigator.clipboard.writeText(code);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) {
      const codeEl = this.querySelector('[data-welcome-offer-code]');
      if (codeEl) {
        const range = document.createRange();
        range.selectNodeContents(codeEl);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
    button.textContent = copied ? this.dataset.copySuccess : this.dataset.copyError;
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => {
      button.textContent = this.dataset.copyLabel;
    }, 2500);
  }
}

if (!customElements.get('s-welcome-offer')) customElements.define('s-welcome-offer', SWelcomeOffer);
